/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { debounce } from 'lodash';
import { observer } from 'mobx-react';
import * as React from 'react';

import { IdropdownQuantityProps } from './DropdownQuantity.props';

interface IdropdownQuantityState {
    currentInput: number;
    isUpdating: boolean;
}

/**
 * Quantity Component - This component is used to add or remove quantity.
 */
@observer
export default class DropdownQuantity extends React.PureComponent<IdropdownQuantityProps, IdropdownQuantityState> {
    public static defaultProps: Partial<IdropdownQuantityProps> = {
        min: 1
    };

    private readonly _minValue: number;

    private readonly inputRef: React.RefObject<HTMLInputElement> = React.createRef<HTMLInputElement>();

    constructor(props: IdropdownQuantityProps) {
        super(props);
        this.state = { currentInput: props.currentCount ?? 1, isUpdating: false };
        this._handleChange = this._handleChange.bind(this);
        this._minValue = this.props.min ?? DropdownQuantity.defaultProps.min!;
    }

    public static getDerivedStateFromProps(props: Readonly<IdropdownQuantityProps>, state: IdropdownQuantityState): IdropdownQuantityState {
        const nextState = { ...state };

        if (props.disabled && !state.isUpdating) {
            nextState.isUpdating = true;
        }

        if (!props.disabled && state.isUpdating) {
            nextState.currentInput = props.currentCount ?? 1;
            nextState.isUpdating = false;
        }
        return nextState;
    }

    public render(): JSX.Element {
        const { max, options } = this.props;

        const currentValue = this.state.currentInput;

        return (
            <select
                id={this.props.id}
                className='quantity-select'
                value={this.state.currentInput}
                onChange={this._handleChange}
                onBlur={this._validateMin}
                aria-live='polite'
                aria-label={`${this.props.inputQuantityAriaLabel}`}
                role='spinbutton'
                aria-valuemin={this._minValue}
                aria-valuemax={max}
                aria-valuenow={currentValue}
            >
                {options.map(n => {
                    return (
                        <option key={n} className='msc-l-dropdown__option' value={n} selected={n === currentValue ? true : false}>
                            {n}
                        </option>
                    );
                })}
            </select>
        );
    }

    private _handleChange(e: React.ChangeEvent<HTMLSelectElement>): void {
        let currentValue = Number.parseInt(e.target.value, 10);
        if (!Number.isNaN(currentValue)) {
            currentValue = Math.min(this.props.max, Math.max(this._minValue, currentValue));
        }
        this.setState({ currentInput: currentValue }, this._handleUpdateState);
    }

    /**
     * Updates html element and triggers callbacks whenever state is changed.
     */
    private readonly _handleUpdateState = () => {
        debounce(() => {
            if (Number.isNaN(this.state.currentInput)) {
                return;
            }

            // Due of usage debouncer, we may ignore onChange return value
            this.props.onChange && this.props.onChange(this.state.currentInput);

            const inputElement =
                this.inputRef && this.inputRef.current && this.inputRef.current instanceof HTMLInputElement && this.inputRef.current;
            if (inputElement) {
                inputElement.setAttribute('aria-valuenow', this.state.currentInput.toString());
                inputElement.setAttribute('value', this.state.currentInput.toString());
            }
        }, 200)();
    };

    private readonly _validateMin = (): void => {
        if (Number.isNaN(this.state.currentInput)) {
            this.setState({ currentInput: this._minValue }, this._handleUpdateState);
        } else {
            const currentValue = Math.min(this.props.max, Math.max(this._minValue, this.state.currentInput));
            if (currentValue !== this.state.currentInput) {
                this.setState({ currentInput: currentValue }, this._handleUpdateState);
            } else {
                this._handleUpdateState();
            }
        }
    };
}
